import { useCallback, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { ApiProvider, AuthProvider, HandoffGate, SsoCallbackGate, sessionController, signInUrl, endReasonOf, useApi, useAuth, appPath, routerPath } from "@ncp/api-client";
import { ContentLanguageProvider, DraftOwner, UiProvider } from "@ncp/ui";
import { useLocale } from "@ncp/i18n";

export const AUTH_STORAGE_KEY = "ncp.backoffice.session";
export const API_BASE = import.meta.env.VITE_API_URL ?? "";

const LOGIN_PATH = "/login";
/** The session of this app (security §3.3): access token in storage, refresh cookie, activity, idle and absolute limits. */
const session = sessionController(AUTH_STORAGE_KEY, { apiBase: API_BASE });
const getToken = () => session.getToken();
const getFreshToken = () => session.freshToken();
const refresh = async () => ["refreshed", "adopted"].includes(await session.refresh());

function ApiBridge({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  return (
    <ApiProvider
      baseUrl={API_BASE}
      getToken={getToken}
      getLocale={() => locale}
      getFreshToken={getFreshToken}
      refresh={refresh}
      onUnauthorized={(reason) => {
        const { pathname, search, hash } = window.location;
        if (session.read()) {
          // The provider's end listener takes the person to the sign-in page with the reason.
          session.end(endReasonOf(reason), { revoke: false });
          return;
        }
        if (routerPath(pathname).startsWith(LOGIN_PATH)) return;
        // Hard navigation on purpose: it also resets in-memory state; the login page returns the user to `from` afterwards.
        window.location.assign(appPath(signInUrl(LOGIN_PATH, null, `${routerPath(pathname)}${search}${hash}`)));
      }}
    >
      {children}
    </ApiProvider>
  );
}

function HandoffPending() {
  return (
    <div className="grid min-h-screen place-items-center bg-sand-50">
      <Loader2 className="size-6 animate-spin text-aegold-700" aria-hidden />
    </div>
  );
}

/** English passages of case content read in Arabic through the platform's cached machine translation (visual review item 1). */
function ContentLanguage({ children }: { children: ReactNode }) {
  const api = useApi();
  const { isAuthenticated } = useAuth();
  const translate = useCallback(
    (texts: string[]) => (isAuthenticated ? api.post<{ translations: Record<string, string> }>("/api/content/translate", { texts, target: "ar" }).then((r) => r.translations) : Promise.resolve({})),
    [api, isAuthenticated],
  );
  return <ContentLanguageProvider translate={translate}>{children}</ContentLanguageProvider>;
}

function LocaleDirection({ children }: { children: ReactNode }) {
  const { dir } = useLocale();
  // Text officers write is kept on the device per signed-in officer until the server has it (S 2.7).
  const { user } = useAuth();
  return <UiProvider dir={dir}><DraftOwner owner={user?.id}><ContentLanguage>{children}</ContentLanguage></DraftOwner></UiProvider>;
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <AuthProvider storageKey={AUTH_STORAGE_KEY} apiBase={API_BASE} loginPath={LOGIN_PATH}>
      <ApiBridge>
        <HandoffGate fallback={<HandoffPending />}>
          <SsoCallbackGate fallback={<HandoffPending />}>
            <LocaleDirection>{children}</LocaleDirection>
          </SsoCallbackGate>
        </HandoffGate>
      </ApiBridge>
    </AuthProvider>
  );
}
